// =============================================================================
// DEAL TYPE REGISTRY - Single Source of Truth for Deal Type Behavior
// =============================================================================
// CR043: Centralizes all deal type classification, rendering config, and behaviors
// NOTE: For deriving deal type from a deal object, use getDealType() 
//       from src/lib/deals/getDealType.ts which handles all fallback logic.
// =============================================================================

import { Gift, Percent, Star, Ticket, HelpCircle, type LucideIcon } from "lucide-react";

// =============================================================================
// DEAL CATEGORY TYPE
// =============================================================================

export type DealCategory = "BONUS" | "DISCOUNT" | "POINTS" | "IN_KIND" | "UNCLASSIFIED";

// =============================================================================
// DEAL TYPE CONFIG INTERFACE
// =============================================================================

export interface DealTypeConfig {
  // Display
  label: string;
  icon: LucideIcon;
  
  // CR500: Semantic theme key for future theming (no behavior change yet)
  themeKey: "bonus" | "discount" | "points" | "inkind" | "unclassified";
  
  // Badge styling (Tailwind classes) - legacy parity
  badgeColors: {
    bg: string;
    text: string;
    border: string;
  };
  
  // Corner ribbon styling - legacy parity
  ribbonBg: string;
  ribbonLabel: string;
  
  // Behavior flags
  showPointsAmount: boolean;
  showBonusAmount: boolean;
  showDiscountPercent: boolean;
  canHaveConversionRate: boolean;
}

// =============================================================================
// DEAL TYPE REGISTRY
// =============================================================================

export const DEAL_TYPE_REGISTRY: Record<DealCategory, DealTypeConfig> = {
  BONUS: {
    label: "Bonus",
    icon: Gift,
    themeKey: "bonus",
    badgeColors: {
      bg: "bg-green-100",
      text: "text-green-800",
      border: "border-green-200",
    },
    ribbonBg: "bg-green-600",
    ribbonLabel: "BONUS",
    showPointsAmount: false,
    showBonusAmount: true,
    showDiscountPercent: false,
    canHaveConversionRate: false,
  },
  
  DISCOUNT: {
    label: "Discount",
    icon: Percent,
    themeKey: "discount",
    badgeColors: {
      bg: "bg-blue-100",
      text: "text-blue-800",
      border: "border-blue-200",
    },
    ribbonBg: "bg-orange-500",
    ribbonLabel: "% BACK",
    showPointsAmount: false,
    showBonusAmount: false,
    showDiscountPercent: true,
    canHaveConversionRate: false,
  },
  
  POINTS: {
    label: "Points",
    icon: Star,
    themeKey: "points",
    badgeColors: {
      bg: "bg-purple-100",
      text: "text-purple-800",
      border: "border-purple-200",
    },
    ribbonBg: "bg-purple-600",
    ribbonLabel: "POINTS",
    showPointsAmount: true,
    showBonusAmount: false,
    showDiscountPercent: false,
    canHaveConversionRate: true,
  },
  
  IN_KIND: {
    label: "In-Kind",
    icon: Ticket,
    themeKey: "inkind",
    badgeColors: {
      bg: "bg-amber-100",
      text: "text-amber-800",
      border: "border-amber-200",
    },
    ribbonBg: "bg-amber-600",
    ribbonLabel: "FREE",
    showPointsAmount: false,
    showBonusAmount: false,
    showDiscountPercent: false,
    canHaveConversionRate: false,
  },
  
  UNCLASSIFIED: {
    label: "Unclassified",
    icon: HelpCircle,
    themeKey: "unclassified",
    badgeColors: {
      bg: "bg-gray-100",
      text: "text-gray-600",
      border: "border-gray-200",
    },
    ribbonBg: "bg-gray-500",
    ribbonLabel: "DEAL",
    showPointsAmount: false,
    showBonusAmount: false,
    showDiscountPercent: false,
    canHaveConversionRate: false,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get deal type config by category
 */
export function getDealTypeConfig(category: DealCategory): DealTypeConfig {
  return DEAL_TYPE_REGISTRY[category] || DEAL_TYPE_REGISTRY.UNCLASSIFIED;
}

/**
 * Get display label for a deal category
 */
export function getDealCategoryLabel(category: DealCategory): string {
  return getDealTypeConfig(category).label;
}

/**
 * Get badge colors for a deal category
 */
export function getDealCategoryColor(category: DealCategory): { bg: string; text: string; border: string } {
  return getDealTypeConfig(category).badgeColors;
}

/**
 * Get icon component for a deal category
 */
export function getDealCategoryIcon(category: DealCategory): LucideIcon {
  return getDealTypeConfig(category).icon;
}

/**
 * Get ribbon background class for a deal category
 */
export function getDealRibbonBg(category: DealCategory): string {
  return getDealTypeConfig(category).ribbonBg;
}

/**
 * Get ribbon label for a deal category
 */
export function getDealRibbonLabel(category: DealCategory): string {
  return getDealTypeConfig(category).ribbonLabel;
}

// =============================================================================
// CATEGORY ICONS MAP (for legacy compatibility)
// =============================================================================

export const CATEGORY_ICONS: Record<DealCategory, LucideIcon> = {
  BONUS: Gift,
  DISCOUNT: Percent,
  POINTS: Star,
  IN_KIND: Ticket,
  UNCLASSIFIED: HelpCircle,
};
